<script lang="ts">
  import Icon from '../icon/icon.svelte';
  import Portrait from './portrait.svelte';
  import { playAudio, SoundType } from './audio.svelte';
  import type { Player } from '../models/player.type';

  interface Props {
    items?: Array<Player>;
    // Index of the card in the centre. Every card's place is worked out from
    // this one number, so there is no per-card state to fall out of step.
    offset?: number;
    change?: (item: Player) => void;
    // Whether the arrow keys drive this carousel. Several live on one screen.
    active?: boolean;
  }

  let { items = [], offset = $bindable(0), change = () => {}, active = false }: Props = $props();

  let swipeStart: number | null = $state(null);
  let swiped = false;

  function go(to: number) {
    const n = items.length;
    if (!n) return;

    const next = ((to % n) + n) % n;
    if (next === offset) return;

    playAudio(SoundType.Swish);
    offset = next;
    change(items[offset]);
  }

  // Card i's place relative to the centre one. Anything past the two
  // neighbours waits out of sight behind the centre.
  function place(i: number, offset: number, n: number) {
    const rel = (i - offset + n) % n;
    if (rel === 0) return 'centre';
    if (rel === 1) return 'right';
    if (rel === n - 1) return 'left';
    return 'back';
  }

  function keydown(event: KeyboardEvent) {
    if (!active) return;
    if (event.key === 'ArrowLeft') go(offset - 1);
    if (event.key === 'ArrowRight') go(offset + 1);
  }

  function pointerdown(event: PointerEvent) {
    swipeStart = event.clientX;
    swiped = false;
  }

  function pointerup(event: PointerEvent) {
    if (swipeStart === null) return;
    const dx = event.clientX - swipeStart;
    swipeStart = null;

    if (Math.abs(dx) > 40) {
      // The click that follows a swipe lands on whichever card is now under
      // the finger, and must not count as picking it.
      swiped = true;
      go(offset + (dx < 0 ? 1 : -1));
    }
  }

  function pick(i: number) {
    if (swiped) {
      swiped = false;
      return;
    }
    go(i);
  }
</script>

<svelte:window onkeydown={keydown} />

<div
  class="carousel"
  onpointerdown={pointerdown}
  onpointerup={pointerup}
  onpointercancel={() => (swipeStart = null)}
>
  <button class="arrow" aria-label="Previous" onclick={() => go(offset - 1)}>
    <Icon name="prevarrow" width="100%" height="100%" />
  </button>

  <div class="cards">
    {#each items as item, index}
      <div
        class="slide {place(index, offset, items.length)}"
        role="button"
        tabindex="-1"
        aria-label={item.title}
        onclick={() => pick(index)}
        onkeydown={() => {}}
      >
        <div
          class="character"
          class:selected={index === offset}
          style="--player-colour:{item.colour}"
        >
          <Portrait data={item} />
        </div>
      </div>
    {/each}
  </div>

  <button class="arrow" aria-label="Next" onclick={() => go(offset + 1)}>
    <Icon name="nextarrow" width="100%" height="100%" />
  </button>
</div>

<style lang="less">
  .carousel {
    // Every distance below is a share of the card size, so the carousel is
    // the same shape on a phone as on a monitor. The depth matters sideways
    // too: pushed back after the 30 degree turn, a card also moves out by half
    // its depth, which a fixed depth made far too wide on a small card.
    --shift: calc(var(--column-size) * 0.2);
    --depth: calc(var(--column-size) * -0.67);

    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    grid-gap: 8px;
    touch-action: pan-y;
  }

  .arrow {
    position: relative;
    z-index: 1;
    width: clamp(32px, calc(var(--column-size) / 5), 50px);
    height: clamp(32px, calc(var(--column-size) / 5), 50px);
    padding: 0;
    background-color: transparent;
    box-shadow: none;

    &:hover,
    &:focus {
      border-color: transparent;
      outline: none;
    }
  }

  .cards {
    position: relative;
    height: var(--column-size);
    min-width: calc(var(--column-size) * 1.8);
    perspective: calc(var(--column-size) * 3.33);
    transform-style: preserve-3d;
    // The side cards sit behind this plane in 3D, so it would take every
    // click meant for them. Swipes are caught on .carousel instead.
    pointer-events: none;
  }

  .slide {
    position: absolute;
    top: 0;
    left: 50%;
    width: var(--column-size);
    height: var(--column-size);
    margin-left: calc(var(--column-size) / -2);
    cursor: pointer;
    pointer-events: auto;
    transition:
      transform 0.5s ease-in-out,
      opacity 0.5s;

    &.centre {
      transform: translateX(0) rotateY(0) translateZ(0);
      cursor: default;
    }
    &.left {
      opacity: 0.5;
      transform: translateX(calc(var(--shift) * -1)) rotateY(30deg) translateZ(var(--depth));
    }
    &.right {
      opacity: 0.5;
      transform: translateX(var(--shift)) rotateY(-30deg) translateZ(var(--depth));
    }
    &.back {
      opacity: 0;
      pointer-events: none;
      transform: translateX(0) rotateY(0) translateZ(calc(var(--depth) * 2));
    }
  }

  .character {
    // The portrait's trait tips would open on a card that is only passing by.
    pointer-events: none;
    filter: saturate(25%);
    transition: filter 0.3s;

    &.selected {
      filter: saturate(100%);
    }
  }
</style>
